import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin } from '../../services/adminAuthService';
import { isAllowedRedirectUrl } from '../../utils/siteLoginRedirect.js';
import '../../styles/admin-ui.css';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [siteAccessDenied, setSiteAccessDenied] = useState(null);
  const redirectTimerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!siteAccessDenied?.suggestedLoginUrl) return;
    if (!isAllowedRedirectUrl(siteAccessDenied.suggestedLoginUrl)) return;
    redirectTimerRef.current = window.setTimeout(() => {
      window.location.assign(siteAccessDenied.suggestedLoginUrl);
    }, 2000);
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, [siteAccessDenied?.suggestedLoginUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSiteAccessDenied(null);
    setLoading(true);

    try {
      const user = await adminLogin(email, password);
      // Check if user must reset password
      if (user.mustResetPassword) {
        // Redirect to reset password page
        navigate('/admin/reset-password', { replace: true });
      } else {
        // Redirect to intended destination or admin root
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get('returnTo');
        
        // Validate returnTo to prevent open redirects - must be an internal path
        let targetPath = '/admin';
        if (returnTo) {
          // Only allow paths that start with /admin or /
          // Reject external URLs, javascript:, etc.
          if (returnTo.startsWith('/admin') || returnTo === '/') {
            targetPath = returnTo;
          } else if (returnTo.startsWith('/') && !returnTo.includes('://') && !returnTo.includes('javascript:')) {
            // Allow other internal admin paths (e.g., /admin/notifications)
            targetPath = returnTo;
          }
        }
        
        navigate(targetPath, { replace: true });
      }
    } catch (err) {
      if (err?.errorCode === 'SITE_ACCESS_DENIED' && err?.details) {
        setSiteAccessDenied({
          message: err.message || "This account can't sign in here. Redirecting you to the correct site…",
          suggestedLoginUrl: err.details.suggestedLoginUrl || null,
        });
      }
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f3f4f6',
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: '3rem',
        borderRadius: '0.5rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px',
      }}>
        <h1 style={{
          fontSize: '1.875rem',
          fontWeight: 'bold',
          marginBottom: '2rem',
          color: '#1f2937',
          textAlign: 'center',
        }}>
          Admin Login
        </h1>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="email" style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '0.5rem',
            }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                outline: 'none',
                backgroundColor: loading ? '#f3f4f6' : '#fff',
              }}
            />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label htmlFor="password" style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '0.5rem',
            }}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                outline: 'none',
                backgroundColor: loading ? '#f3f4f6' : '#fff',
              }}
            />
          </div>
          {error && (
            <div style={{
              padding: '0.75rem',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              marginBottom: '1.5rem',
            }}>
              {error}
              {siteAccessDenied?.suggestedLoginUrl && isAllowedRedirectUrl(siteAccessDenied.suggestedLoginUrl) && (
                <div style={{ marginTop: '0.5rem' }}>
                  <a href={siteAccessDenied.suggestedLoginUrl} style={{ color: '#2563eb' }}>
                    Go to correct site
                  </a>
                  <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>(redirecting in 2s…)</span>
                </div>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem 1.5rem',
              backgroundColor: loading ? '#9ca3af' : '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}
